import { Controller, useFormContext } from 'react-hook-form'
import DateTimeNow from 'components/RobComponents/DateTimeNow'
import { Input } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import RequiredLabel from 'components/RequiredLabel'
import { useTranslation } from 'react-i18next'

// ---------------------------------------------------------------------------
// Mock values — will be replaced by real data from the vessel info / bunker
// sections once those are implemented.
// ---------------------------------------------------------------------------
const MOCK_BUNKER_DATA = {
  bunkerDeliveryNote: 'BDN-2026-008815',
  dateOfSampling: '11-06-2026',
  deliveryDate: '11-06-2026',
  fuelGradePerBdn: 'VLSFO',
  imoNo: '9812345',
  nameOfSurveyor: 'James Eriksson',
  orderReferenceNo: 'ORD-2026-004421',
  physicalSupplier: 'Global Marine Fuels Ltd.',
  placeOfSurveyPort: 'Rotterdam, Netherlands',
  shipName: 'MV Atlantic Harmony',
  surveyCompany: 'Nordic Survey Services',
  terminalName: 'Rotterdam Fuel Terminal B',
}

// Read-only display cell (grey background, no interaction)
const ReadOnlyValue = ({ value }) => (
  <span className="lab-form-row__readonly">{value}</span>
)

ReadOnlyValue.propTypes = {
  value: PropTypes.string,
}

const READ_ONLY_ROWS = [
  { labelKey: 'rob.survey.bunker.lab.bunker.shipName', mockKey: 'shipName' },
  { labelKey: 'rob.survey.bunker.lab.bunker.imoNo', mockKey: 'imoNo' },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.terminalName',
    mockKey: 'terminalName',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.placeOfSurveyPort',
    mockKey: 'placeOfSurveyPort',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.orderReferenceNo',
    mockKey: 'orderReferenceNo',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.bunkerDeliveryNote',
    mockKey: 'bunkerDeliveryNote',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.deliveryDate',
    mockKey: 'deliveryDate',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.fuelGradePerBdn',
    mockKey: 'fuelGradePerBdn',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.physicalSupplier',
    mockKey: 'physicalSupplier',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.nameOfSurveyor',
    mockKey: 'nameOfSurveyor',
  },
  {
    labelKey: 'rob.survey.bunker.lab.bunker.surveyCompany',
    mockKey: 'surveyCompany',
  },
]

const LabBunkerSection = ({ saveAttempted }) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { submitCount },
  } = useFormContext()
  const showErrors = saveAttempted || submitCount > 0

  return (
    <div className="lab-card">
      <div className="lab-card__header">
        {t('rob.survey.bunker.lab.bunker.title')}
      </div>
      <div className="lab-card__body">
        {/* Read-only rows */}
        {READ_ONLY_ROWS.map(({ labelKey, mockKey }) => (
          <div className="lab-form-row" key={mockKey}>
            <span className="lab-form-row__label">{t(labelKey)}</span>
            <ReadOnlyValue value={MOCK_BUNKER_DATA[mockKey]} />
          </div>
        ))}

        {/* Lab Company — EDITABLE (required) */}
        <div className="lab-form-row">
          <span className="lab-form-row__label">
            <RequiredLabel
              required
              text={t('rob.survey.bunker.lab.bunker.labCompany')}
            />
          </span>
          <Controller
            control={control}
            name="laboratory.bunker.labCompany"
            render={({ field, fieldState }) => (
              <div className="lab-form-row__field-stack">
                <Input
                  {...field}
                  className="lab-form-row__input"
                  status={showErrors && fieldState.error ? 'error' : undefined}
                />
                {showErrors && fieldState.error?.message && (
                  <span className="lab-form-row__error">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Date of Sampling — read-only */}
        <div className="lab-form-row">
          <span className="lab-form-row__label">
            {t('rob.survey.bunker.lab.bunker.dateOfSampling')}
          </span>
          <ReadOnlyValue value={MOCK_BUNKER_DATA.dateOfSampling} />
        </div>

        {/* Testing Completed — EDITABLE (date) */}
        <div className="lab-form-row">
          <span className="lab-form-row__label">
            <RequiredLabel
              required
              text={t('rob.survey.bunker.lab.bunker.testingCompleted')}
            />
          </span>
          <Controller
            control={control}
            name="laboratory.bunker.testingCompleted"
            render={({ field: { onChange, value }, fieldState }) => (
              <div className="lab-form-row__field-stack">
                <DateTimeNow
                  allowFuture={false}
                  onChange={onChange}
                  showNow={true}
                  showTime={false}
                  value={value ?? ''}
                />
                {showErrors && fieldState.error?.message && (
                  <span className="lab-form-row__error">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  )
}

LabBunkerSection.propTypes = {
  saveAttempted: PropTypes.bool,
}

LabBunkerSection.defaultProps = {
  saveAttempted: false,
}

export default LabBunkerSection
